import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { getTemplate } from "@/templates/registry";
import {
  Monitor, Tablet, Smartphone, ArrowLeft, Wand2, Loader2, ExternalLink, Menu, X,
} from "lucide-react";

const DEVICES = {
  desktop: { icon: Monitor, label: "Desktop", width: "100%", max: "100%" },
  tablet: { icon: Tablet, label: "Tablet", width: "768px", max: "768px" },
  mobile: { icon: Smartphone, label: "Mobile", width: "390px", max: "390px" },
};

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/solutions", label: "Solutions" },
  { to: "/marketplace", label: "Our Work" },
  { to: "/services", label: "Services" },
  { to: "/start", label: "Start a Project" },
];

export default function TemplateDemo() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const reg = getTemplate(slug);
  const [dbTpl, setDbTpl] = useState(undefined); // undefined = loading, null = not in DB
  const [device, setDevice] = useState("desktop");
  const [menuOpen, setMenuOpen] = useState(false);
  const iframeRef = useRef(null);
  const previewAreaRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [frameH, setFrameH] = useState(844);
  const [bust, setBust] = useState(0);      // cache-bust counter → forces iframe remount
  const [demoFailed, setDemoFailed] = useState(false);

  const MAX_HEAL = 3;

  // A served ATF demo renders into an EMPTY shell via config.js + main.js. A fully-loaded demo has
  // thousands of chars of text; an un-populated shell (scripts didn't run) has only the footer/banner
  // (~90-160 chars). Treat too-little text as an incomplete render that needs a reload.
  const isIframeIncomplete = () => {
    const f = iframeRef.current;
    if (!f) return false;
    try {
      const doc = f.contentDocument;
      if (!doc) return false; // cross-origin — cannot inspect, assume ok
      const body = doc.body;
      if (!body) return true;
      return (body.innerText || "").trim().length < 300;
    } catch (e) {
      return false; // cross-origin access blocked — assume ok
    }
  };

  // On a good (fully-rendered) load, clear any failed state.
  const handleFrameLoad = () => {
    setTimeout(() => { if (!isIframeIncomplete()) setDemoFailed(false); }, 600);
  };

  useEffect(() => {
    if (!slug) { setDbTpl(null); return; }
    api.get(`/templates/${slug}`).then((r) => setDbTpl(r.data || null)).catch(() => setDbTpl(null));
  }, [slug]);

  // Emulate the true device WIDTH (768/390); scale down only when the viewport is narrower than the
  // device. The frame fills the available HEIGHT and the demo scrolls inside it (device-preview style).
  useEffect(() => {
    if (device === "desktop") { setScale(1); return; }
    const frameW = parseInt(DEVICES[device].width, 10);
    const compute = () => {
      const el = previewAreaRef.current;
      if (!el) return;
      const availW = el.clientWidth - 24;
      const availH = el.clientHeight - 24;
      const s = Math.min(1, availW / frameW) || 1;
      setScale(s);
      setFrameH(Math.max(560, Math.round(availH / s)));
    };
    compute();
    const el = previewAreaRef.current;
    const ro = el ? new ResizeObserver(compute) : null;
    if (ro && el) ro.observe(el);
    window.addEventListener("resize", compute);
    return () => { if (ro) ro.disconnect(); window.removeEventListener("resize", compute); };
  }, [device]);

  // DB record is the source of truth (same record as Home/Marketplace/Detail).
  const tpl = dbTpl
    ? {
        id: dbTpl.id, slug: dbTpl.slug || dbTpl.id,
        name: dbTpl.title || reg?.name || "Template",
        description: dbTpl.description || reg?.description || "",
        category: dbTpl.category || reg?.category || "Business",
        price: dbTpl.template_price ?? reg?.price ?? 0,
      }
    : (dbTpl === null ? reg : null);
  // DB Factory products serve a static demo (/api/demo/{id}); registry prototypes render via /t/{slug}.
  const servedDemo = dbTpl && dbTpl.live_demo && dbTpl.id ? `${process.env.REACT_APP_BACKEND_URL}/api/demo/${dbTpl.id}/` : null;
  const demoSrc = servedDemo || (reg ? `/t/${slug}` : null);
  // Cache-busted URL: changing `bust` remounts the iframe (via key) and reloads the demo.
  const bustedSrc = demoSrc ? `${demoSrc}${demoSrc.includes("?") ? "&" : "?"}v=${bust}` : null;

  // Watchdog: independent of iframe onLoad (which does NOT fire in the blank-mount race).
  // After bounded delays, inspect the iframe doc; if still blank, bump `bust` to remount.
  // After MAX_HEAL attempts, surface a manual "Reload demo" fallback.
  useEffect(() => {
    if (!bustedSrc) return;
    // Poll for completeness; catch population the moment it happens. Give each mount ~2.2s to load
    // its ~110KB of scripts before remounting (cache-bust). After MAX_HEAL remounts, show fallback.
    let elapsed = 0;
    const step = 600;
    const id = setInterval(() => {
      elapsed += step;
      if (!isIframeIncomplete()) { setDemoFailed(false); clearInterval(id); return; }
      if (elapsed >= 2200) {
        clearInterval(id);
        if (bust < MAX_HEAL) setBust((b) => b + 1);
        else setDemoFailed(true);
      }
    }, step);
    return () => clearInterval(id);
  }, [bustedSrc, bust]);


  if (dbTpl === undefined && !reg) {
    return <div className="min-h-screen bg-navy grid place-items-center text-gray-400" data-testid="demo-loading"><Loader2 className="animate-spin text-gold" size={28} /></div>;
  }
  if (!tpl) {
    return (
      <div className="min-h-screen bg-navy grid place-items-center text-gray-400" data-testid="demo-not-found">
        Template not found. <button onClick={() => navigate("/marketplace")} className="text-gold ml-2 underline">Back to Our Work</button>
      </div>
    );
  }

  const d = DEVICES[device];

  const demoContent = demoSrc ? (
    <>
      <iframe
        key={bustedSrc}
        ref={iframeRef}
        title={tpl.name}
        src={bustedSrc}
        onLoad={handleFrameLoad}
        className="w-full h-full border-0 block"
        data-testid="demo-iframe"
      />
      {demoFailed && (
        <div className="absolute inset-0 grid place-items-center bg-white p-8" data-testid="demo-reload-fallback">
          <div className="text-center max-w-sm">
            <p className="text-navy font-heading text-lg mb-1">Demo didn't load</p>
            <p className="text-gray-500 text-sm mb-4">Sometimes the preview needs a nudge. Give it another try.</p>
            <button
              onClick={() => { setDemoFailed(false); setBust((b) => b + 1); }}
              data-testid="demo-reload-btn"
              className="inline-flex items-center gap-2 bg-gold text-navy text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-gold-light transition-colors"
            >
              <Loader2 size={15} /> Reload demo
            </button>
          </div>
        </div>
      )}
    </>
  ) : (
    <div className="w-full h-full grid place-items-center bg-white p-8" data-testid="demo-unavailable">
      <div className="text-center max-w-sm">
        <p className="text-navy font-heading text-lg mb-1">No live demo available yet</p>
        <p className="text-gray-500 text-sm">This product's interactive demo appears here once its website files are uploaded in Admin → Templates.</p>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 flex flex-col bg-navy z-50" data-testid="template-demo-page">
      {/* CONTROL BAR */}
      <header className="shrink-0 bg-navy-900 border-b border-white/10">
        <div className="px-4 md:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <button onClick={() => navigate("/marketplace")} data-testid="demo-back-btn"
              className="inline-flex items-center gap-2 text-gray-300 hover:text-gold transition-colors text-sm shrink-0">
              <ArrowLeft size={16} /> <span className="hidden sm:inline">Back to Our Work</span>
            </button>
            <div className="hidden md:block h-6 w-px bg-white/10" />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="font-heading text-white text-base md:text-lg truncate" data-testid="demo-template-name">{tpl.name}</h1>
                <span className="text-[10px] uppercase tracking-wider text-gold border border-gold/40 px-2 py-0.5 rounded-sm shrink-0" data-testid="demo-template-category">{tpl.category}</span>
              </div>
              <p className="text-gray-500 text-xs truncate hidden sm:block" data-testid="demo-template-desc">{tpl.description}</p>
            </div>
          </div>

          {/* DEVICE TOGGLE */}
          <div className="hidden md:flex items-center gap-1 bg-navy-800 border border-white/10 rounded-full p-1" data-testid="demo-device-toggle">
            {Object.entries(DEVICES).map(([key, dev]) => (
              <button key={key} onClick={() => setDevice(key)} data-testid={`demo-device-${key}`} aria-label={dev.label}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${device === key ? "bg-gold text-navy" : "text-gray-400 hover:text-white"}`}>
                <dev.icon size={14} /> <span className="hidden lg:inline">{dev.label}</span>
              </button>
            ))}
          </div>

          {/* ACTIONS (desktop) */}
          <div className="hidden md:flex items-center gap-2 shrink-0">
            <button onClick={() => navigate(`/start?template=${tpl.slug}`)} data-testid="demo-use-template-btn"
              className="inline-flex items-center gap-2 bg-gold text-navy text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-gold-light transition-colors">
              <Wand2 size={15} /> <span className="hidden sm:inline">Build This for My Business</span>
            </button>
          </div>

          {/* HAMBURGER (mobile) */}
          <button className="md:hidden text-white shrink-0 p-1" onClick={() => setMenuOpen((v) => !v)}
            data-testid="demo-menu-toggle" aria-label={menuOpen ? "Close menu" : "Open menu"} aria-expanded={menuOpen}>
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* mobile device toggle */}
        <div className="md:hidden flex items-center justify-center gap-1 pb-3">
          {Object.entries(DEVICES).map(([key, dev]) => (
            <button key={key} onClick={() => setDevice(key)} data-testid={`demo-device-m-${key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${device === key ? "bg-gold text-navy" : "text-gray-400 border border-white/10"}`}>
              <dev.icon size={13} /> {dev.label}
            </button>
          ))}
        </div>

        {/* MOBILE MENU — site nav + actions */}
        {menuOpen && (
          <div className="md:hidden bg-navy-900 border-t border-white/10 px-5 py-5 flex flex-col gap-1" data-testid="demo-mobile-menu">
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setMenuOpen(false)}
                data-testid={`demo-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="py-2.5 text-gray-200 hover:text-gold transition-colors">
                {l.label}
              </Link>
            ))}
            <div className="h-px bg-white/10 my-3" />
            <button onClick={() => { setMenuOpen(false); navigate(`/start?template=${tpl.slug}`); }} data-testid="demo-use-template-btn-m"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-navy text-sm font-semibold px-4 py-3 rounded-full hover:bg-gold-light transition-colors">
              <Wand2 size={15} /> Build This for My Business
            </button>
          </div>
        )}
      </header>

      {/* PREVIEW FRAME */}
      <div
        ref={previewAreaRef}
        className={`flex-1 min-h-0 bg-[#0a1020] relative ${device === "desktop" ? "overflow-auto" : "overflow-hidden flex justify-center items-start md:items-center p-3 md:p-6"}`}
      >
        {device === "desktop" ? (
          <div className="absolute inset-0 bg-white overflow-hidden" data-testid="demo-preview-frame">
            {demoContent}
          </div>
        ) : (
          <div style={{ width: parseInt(d.width, 10) * scale, height: frameH * scale }} className="relative shrink-0 mx-auto">
            <div
              className="bg-white shadow-2xl overflow-hidden absolute top-0 left-0"
              style={{
                width: parseInt(d.width, 10), height: frameH,
                borderRadius: 18, border: "8px solid #1a2338", boxSizing: "border-box",
                transform: `scale(${scale})`, transformOrigin: "top left",
              }}
              data-testid="demo-preview-frame"
            >
              {demoContent}
            </div>
          </div>
        )}
      </div>

      {/* FOOTNOTE */}
      <div className="shrink-0 bg-navy-900 border-t border-white/10 px-6 py-2 text-center text-xs text-gray-500 hidden md:flex items-center justify-center gap-2">
        <ExternalLink size={12} /> Live interactive demo — scroll, click and resize. This is exactly how your site behaves after launch.
      </div>
    </div>
  );
}
